import pptxgen from 'pptxgenjs'
import XLSX from 'xlsx'

// Renders the assistant's deck (see lib/claude_report_deck.rb) as a PowerPoint or an
// Excel file in the browser. All numbers arrive already calculated.

const COLORS = {
  dark: '24262B', card: '2E3036', slate: '3A3D44', gold: 'B08D57', white: 'FFFFFF',
  mist: 'C9CCD3', pale: 'D8DAE0', gray: '5B6270', dim: '8A8F99',
  stripe: 'F5EFE6', rule: 'C7C1B3', up: '6B7A3F', down: '8C1D18'
}
const FONT = 'Arial'

// Slide is 13.33 x 7.5 (LAYOUT_WIDE).
const addText = (slide, text, options) => {
  slide.addText(text, {fontFace: FONT, margin: 0, isTextBox: true, ...options})
}

const addHeading = (slide, data, dark) => {
  addText(slide, data.title, {x: 0.6, y: 0.4, w: 9.6, h: 0.65, fontSize: 27, bold: true, color: dark ? COLORS.white : COLORS.dark, fit: 'shrink'})
  if (data.subtitle) {
    addText(slide, data.subtitle, {x: 0.6, y: 1.02, w: 9.6, h: 0.42, fontSize: 12, bold: true, italic: true, color: dark ? COLORS.mist : COLORS.gray, valign: 'top'})
  }
}

const addFooter = (slide, left, right, dark) => {
  const color = dark ? COLORS.dim : COLORS.gray
  addText(slide, left, {x: 0.5, y: 7.08, w: 7, h: 0.28, fontSize: 9, bold: true, color})
  addText(slide, right, {x: 7.83, y: 7.08, w: 5, h: 0.28, fontSize: 9, bold: true, color, align: 'right'})
}

const addAgenda = (pptx, data) => {
  const slide = pptx.addSlide()
  slide.background = {color: COLORS.dark}
  addHeading(slide, data, true)
  const count = data.items.length
  const gap = 0.14
  const h = Math.min(0.82, (5.4 - gap * (count - 1)) / Math.max(count, 1))
  data.items.forEach((item, i) => {
    const y = 1.75 + i * (h + gap)
    const size = Math.min(0.52, h - 0.2)
    slide.addShape(pptx.ShapeType.roundRect, {x: 0.6, y, w: 12.1, h, rectRadius: 0.06, fill: {color: COLORS.card}})
    addText(slide, String(i + 1), {
      shape: pptx.ShapeType.ellipse, x: 0.9, y: y + (h - size) / 2, w: size, h: size, fill: {color: COLORS.gold},
      fontSize: 18, bold: true, color: COLORS.dark, align: 'center', valign: 'middle'
    })
    addText(slide, item, {x: 1.7, y, w: 10.7, h, fontSize: 16, bold: true, color: COLORS.white, valign: 'middle'})
  })
}

// Excel number formats keep small bars honest: $414K, not $0M.
const chartFormat = (kind) => {
  if (kind === 'money') return '[>=1000000]$0.0,,"M";[>=1000]$0,"K";$0'
  if (kind === 'count') return '[>=1000000]0.0,,"M";[>=1000]0,"K";0'
  return '0.0"%"'
}

const addChart = (pptx, slide, chart) => {
  if (chart.labels.length === 0) return
  slide.addChart(pptx.ChartType.bar, [{name: chart.name, labels: chart.labels, values: chart.values}], {
    x: 0.4, y: 1.55, w: 6.1, h: 5.25,
    barDir: 'bar', barGapWidthPct: 30, chartColors: [COLORS.gold],
    catAxisOrientation: 'maxMin', catAxisLabelFontFace: FONT, catAxisLabelFontSize: 10, catAxisLabelFontBold: true, catAxisLabelColor: COLORS.dark,
    valAxisHidden: true, valGridLine: {style: 'none'}, catGridLine: {style: 'none'},
    showValue: true, dataLabelPosition: 'outEnd', dataLabelFormatCode: chartFormat(chart.kind),
    dataLabelFontFace: FONT, dataLabelFontSize: 9, dataLabelFontBold: true, dataLabelColor: COLORS.dark,
    showLegend: false
  })
}

const changeColor = (text) => {
  if (text.startsWith('+')) return COLORS.up
  if (text.startsWith('-')) return COLORS.down
  return COLORS.gray
}

const addTable = (slide, table, notes) => {
  const count = table.columns.length
  const otherW = count > 6 ? 0.64 : count > 5 ? 0.78 : 1.05
  const labelW = 6.25 - (count - 1) * otherW
  const fontSize = count > 6 ? 8 : 9
  const rowH = Math.min(0.5, 4.75 / (table.rows.length + 2))
  const border = {type: 'solid', pt: 0.5, color: COLORS.rule}

  const cell = (text, i, options) => ({text, options: {
    fontFace: FONT, fontSize, border, valign: 'middle', margin: 0.04, align: i === 0 ? 'left' : 'right', ...options
  }})

  const header = table.columns.map((text, i) => cell(text, i, {bold: true, color: COLORS.white, fill: {color: COLORS.dark}}))
  const body = table.rows.map((row, r) => row.map((text, i) => {
    const kind = table.kinds[i - 1]
    let color = COLORS.dark
    if (kind === 'change') color = changeColor(text)
    return cell(text, i, {bold: i === 0, color, fill: {color: r % 2 === 1 ? COLORS.stripe : COLORS.white}})
  }))
  const total = table.total.map((text, i) => cell(text, i, {
    bold: true, fontSize: count > 6 ? fontSize + 1 : fontSize + 2, fill: {color: COLORS.stripe},
    color: table.kinds[i - 1] === 'change' ? changeColor(text) : COLORS.dark
  }))

  slide.addTable([header, ...body, total], {
    x: 6.6, y: 1.55, w: 6.25, colW: [labelW, ...Array(count - 1).fill(otherW)], rowH, autoPage: false
  })

  if (notes.length > 0) {
    addText(slide, notes.join('  ·  '), {x: 6.6, y: 6.62, w: 6.25, h: 0.42, fontSize: 8, italic: true, color: COLORS.gray, valign: 'top', fit: 'shrink'})
  }
}

const addTableSlide = (pptx, data, footer) => {
  const slide = pptx.addSlide()
  addHeading(slide, data, false)
  if (data.tag) {
    addText(slide, data.tag, {
      shape: pptx.ShapeType.roundRect, rectRadius: 0.06, x: 10.55, y: 0.5, w: 2.2, h: 0.5, fill: {color: COLORS.gold},
      fontSize: 11, bold: true, color: COLORS.white, align: 'center', valign: 'middle', fit: 'shrink'
    })
  }
  addChart(pptx, slide, data.chart)
  addTable(slide, data.table, data.notes)
  addFooter(slide, data.title, footer, false)
}

const addPoints = (pptx, data, footer) => {
  const slide = pptx.addSlide()
  slide.background = {color: COLORS.dark}
  addHeading(slide, data, true)
  const items = data.items
  const grid = items.length === 4

  items.forEach((item, i) => {
    const w = grid ? 5.9 : 12.1
    const h = grid ? 2.45 : Math.min(1.6, 5.2 / items.length - 0.15)
    const x = grid && i % 2 === 1 ? 6.8 : 0.6
    const y = grid ? 1.75 + (i < 2 ? 0 : 2.6) : 1.75 + i * (h + 0.15)
    slide.addShape(pptx.ShapeType.roundRect, {x, y, w, h, rectRadius: 0.06, fill: {color: COLORS.card}})
    addText(slide, String(i + 1), {
      shape: pptx.ShapeType.ellipse, x: x + 0.25, y: y + 0.25, w: 0.55, h: 0.55, fill: {color: COLORS.gold},
      fontSize: 18, bold: true, color: COLORS.dark, align: 'center', valign: 'middle'
    })
    addText(slide, item.title, {x: x + 1, y: y + 0.2, w: w - 1.2, h: 0.6, fontSize: grid ? 15 : 18, bold: true, color: COLORS.white, valign: 'middle', fit: 'shrink'})
    addText(slide, item.body, {x: x + 1, y: y + 0.82, w: w - 1.2, h: h - 0.95, fontSize: 12, color: COLORS.pale, valign: 'top', fit: 'shrink'})
  })
  addFooter(slide, data.title, footer, true)
}

const addAsk = (pptx, data, footer) => {
  const slide = pptx.addSlide()
  slide.background = {color: COLORS.dark}
  addHeading(slide, data, true)

  const {mix, callout} = data
  const groups = data.groups || []

  if (mix && mix.parts && mix.parts.length > 0) {
    addText(slide, (mix.title || '').toUpperCase(), {x: 0.6, y: 1.65, w: 5.6, h: 0.3, fontSize: 13, bold: true, color: COLORS.gold})
    slide.addChart(pptx.ChartType.doughnut, [{name: mix.title || 'Mix', labels: mix.parts.map(p => p.label), values: mix.parts.map(p => Number(p.value))}], {
      x: 0.6, y: 1.95, w: 5.6, h: 2.4, holeSize: 50, chartColors: [COLORS.gold, COLORS.slate, COLORS.dim, COLORS.pale],
      showPercent: true, showLegend: true, legendPos: 'b', legendColor: COLORS.pale, legendFontSize: 11,
      dataLabelColor: COLORS.white, dataLabelFontSize: 13, dataLabelFontBold: true
    })
    if (mix.note) {
      addText(slide, mix.note, {x: 0.6, y: 4.4, w: 5.6, h: 0.7, fontSize: 10, italic: true, bold: true, color: COLORS.mist, valign: 'top', fit: 'shrink'})
    }
  }

  groups.slice(0, 2).forEach((group, g) => {
    const top = 1.65 + g * 1.75
    addText(slide, (group.title || '').toUpperCase(), {x: 6.8, y: top, w: 5.9, h: 0.3, fontSize: 13, bold: true, color: COLORS.gold})
    ;(group.cards || []).slice(0, 2).forEach((card, c) => {
      const x = 6.8 + c * 3.03
      const isTbd = String(card.value).toUpperCase() === 'TBD'
      slide.addShape(pptx.ShapeType.roundRect, {x, y: top + 0.35, w: 2.87, h: 1.25, rectRadius: 0.06, fill: {color: COLORS.slate}})
      addText(slide, String(card.value), {x: x + 0.1, y: top + 0.42, w: 2.67, h: 0.55, fontSize: 19, bold: true, color: isTbd ? COLORS.dim : COLORS.gold, align: 'center', valign: 'middle', fit: 'shrink'})
      addText(slide, card.caption || '', {x: x + 0.1, y: top + 0.97, w: 2.67, h: 0.58, fontSize: 10, bold: true, color: COLORS.pale, align: 'center', valign: 'top', fit: 'shrink'})
    })
  })

  if (callout) {
    slide.addShape(pptx.ShapeType.roundRect, {x: 0.6, y: 5.25, w: 12.1, h: 1.65, rectRadius: 0.06, fill: {color: COLORS.card}})
    addText(slide, callout.title || '', {x: 0.95, y: 5.35, w: 11.4, h: 0.38, fontSize: 14, bold: true, color: COLORS.gold})
    addText(slide, callout.body || '', {x: 0.95, y: 5.75, w: 11.4, h: 1.05, fontSize: 12, bold: true, color: COLORS.pale, valign: 'top', fit: 'shrink'})
  }
  addFooter(slide, data.title, footer, true)
}

const fileBase = (deck) => (deck.title || '').replace(/[^\w -]+/g, '').trim() || 'presentation'

const downloadDeck = (deck) => {
  const pptx = new pptxgen()
  pptx.layout = 'LAYOUT_WIDE'
  deck.slides.forEach((slide) => {
    if (slide.type === 'agenda') addAgenda(pptx, slide)
    else if (slide.type === 'points') addPoints(pptx, slide, deck.footer)
    else if (slide.type === 'ask') addAsk(pptx, slide, deck.footer)
    else addTableSlide(pptx, slide, deck.footer)
  })
  return pptx.writeFile({fileName: `${fileBase(deck)}.pptx`})
}

// One sheet per table slide, as real numbers rather than "$27.77M".
const deckWorkbook = (deck) => {
  const book = XLSX.utils.book_new()
  deck.slides.filter(s => s.type === 'table').forEach((slide, i) => {
    const rows = [slide.table.columns, ...slide.table.raw, slide.table.sums, [], ...slide.notes.map(n => [n])]
    const name = `${i + 1} ${slide.title}`.replace(/[\\/?*[\]:]/g, ' ').slice(0, 31)
    XLSX.utils.book_append_sheet(book, XLSX.utils.aoa_to_sheet(rows), name)
  })
  return book
}

const downloadDeckExcel = (deck) => {
  XLSX.writeFile(deckWorkbook(deck), `${fileBase(deck)}.xlsx`)
}

export {downloadDeck, downloadDeckExcel, deckWorkbook}
